import React from "react";
import { MessageSquare, Bot, CircleSlash } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { VideoEmbed } from "../components/VideoEmbed";
import { CtaBand } from "./Home";
import { USE_CASES } from "../data/content";

const Overline = ({ children }) => (
  <span className="font-mono-el text-xs tracking-[0.25em] uppercase text-[#4FC3F7]">{children}</span>
);

export default function UseCases() {
  return (
    <div data-testid="usecases-page">
      {/* HERO */}
      <section className="relative pt-40 pb-20 grid-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>Use Cases</Overline>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-light leading-tight tracking-tight text-white max-w-4xl">
              Agents in action.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#94A3B8] max-w-3xl leading-relaxed">
              In every case, the team states an intent. Apeiron's agents orchestrate the entire data
              lifecycle beneath it — and the team never touches the plumbing.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CASES */}
      <section className="relative pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-8">
          {USE_CASES.map((uc, i) => (
            <Reveal
              key={uc.title}
              delay={40}
              className="relative bg-white/5 border border-white/10 overflow-hidden"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12">
                {/* header rail */}
                <div className="lg:col-span-4 p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-white/10 bg-gradient-to-br from-[#1565C0]/15 to-transparent">
                  <span className="font-mono-el text-xs tracking-[0.2em] uppercase text-[#4FC3F7]">
                    {String(i + 1).padStart(2, "0")} · {uc.industry}
                  </span>
                  <h2 className="mt-4 text-2xl md:text-3xl font-medium text-white leading-snug">
                    {uc.title}
                  </h2>
                  <p className="mt-4 text-sm text-[#64748B] font-mono-el">{uc.profile}</p>
                  <p className="mt-8 text-sm text-[#94A3B8] leading-relaxed border-t border-white/10 pt-6">
                    {uc.outcome}
                  </p>
                </div>

                {/* body */}
                <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2">
                  <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-white/10">
                    <div className="flex items-center gap-2 text-[#94A3B8] mb-5">
                      <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
                      <span className="font-mono-el text-xs tracking-[0.15em] uppercase">
                        What the team expressed
                      </span>
                    </div>
                    <ul className="space-y-4">
                      {uc.expressed.map((e) => (
                        <li key={e} className="flex gap-3 text-sm text-[#CBD5E1] leading-relaxed">
                          <span className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-[#475569]" />
                          {e}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-8 md:p-10">
                    <div className="flex items-center gap-2 text-[#4FC3F7] mb-5">
                      <Bot className="w-4 h-4" strokeWidth={1.5} />
                      <span className="font-mono-el text-xs tracking-[0.15em] uppercase">
                        What the agent orchestrated
                      </span>
                    </div>
                    <ul className="space-y-4">
                      {uc.orchestrated.map((o) => (
                        <li
                          key={o}
                          className="flex gap-3 text-sm text-white leading-relaxed font-mono-el"
                        >
                          <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-[#4FC3F7]" />
                          {o}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {uc.videoId && (
                <div className="border-t border-white/10 p-6 md:p-10 bg-[#020817]/40">
                  <div className="flex items-center gap-2 text-[#4FC3F7] mb-5">
                    <Bot className="w-4 h-4" strokeWidth={1.5} />
                    <span className="font-mono-el text-xs tracking-[0.15em] uppercase">
                      Watch the {uc.industry} demo
                    </span>
                  </div>
                  <VideoEmbed videoId={uc.videoId} title={`${uc.industry} — ${uc.title} demo`} />
                </div>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      {/* NEVER TOUCHED STRIP */}
      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="flex flex-col md:flex-row items-start md:items-center gap-4 text-[#64748B] border-t border-white/10 pt-10">
            <CircleSlash className="w-5 h-5 text-[#475569] shrink-0" strokeWidth={1.5} />
            <p className="text-sm md:text-base leading-relaxed">
              Across every engagement, teams never touched cluster configuration, reverse-ETL
              pipelines, manual data tagging, or month-end cost reconciliation. The agent handles
              the lifecycle — humans stay in the loop on intent and spend.
            </p>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
